import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { changeExtensionToPdf } from "../../../src/taskpane/components/helpers/stringHelper";
import { SharePointFolderContentTypeIds, validPdfExtensions } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { ConvertAndSaveAsPDFRequest, FilePreCommitRequest } from "../../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { IFilePreCommitResult } from "../../../src/taskpane/interfaces/IFilePreCommitResult";
import { getFilePreCommitInfo } from "../../../src/taskpane/services/SharePointRestServices";
import { GridProps } from "../useContentManagerBody";
import { apiFileConvertAndSaveAsPDF } from "../../../src/taskpane/services/ApiService";
import { getDriveRootWebUrl, getOneDriveRootDetails } from "../../../src/taskpane/services/GraphService";
import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";

export function useMenuItem_SaveAsPDF(
  selectedItems: any[],
  waitFor: boolean,
  setWaitFor: (waitFor: boolean) => void,
  isInOneDrive: boolean,
  setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
  setErrorMessage?: (errorMessage: string) => void,
  openDuplicateFileVersionSelectorModal?: (isDuplicate: boolean, showVersion: boolean) => void,
  reloadGrid?: (gridData?: GridProps) => void) {
  const languageStrings = React.useContext(LanguageContext);

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

  const [menuItem_SaveAsPDF, setMenuItem_SaveAsPDF] = React.useState<IContextualMenuItem>();

  React.useEffect(() => {
    if (selectedItems?.length === 1
      && selectedItems[0].EffectiveBasePermissions?.canOpenItems
      && !selectedItems[0].ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)
      && validPdfExtensions.some(validExt => selectedItems[0][".fileType"]?.replace(".", "")?.toLowerCase() === validExt)) {
      setMenuItem_SaveAsPDF(
        {
          key: "saveAsPDF",
          disabled: (waitFor === true || isBusySaving === true),
          text: `${languageStrings.SaveAsPdf}`,
          iconProps: { iconName: "PDF" },
          onClick: () => {
            initiateSaveAndConvertAsPdfFile(selectedItems[0]);
          }
        }
      );
    } else {
      setMenuItem_SaveAsPDF(null);
    }
  }, [selectedItems, waitFor, currentDirectory, oneDriveRootDetails]);

  const initiateSaveAndConvertAsPdfFile = async (selectedItem: any) => {
    setWaitFor(true);

    let driveId: string;
    let siteUrl: string;
    let sharePointToken: string;

    if (isInOneDrive || currentDirectory.webUrl?.includes("sharepoint.com/personal/")) {
      let oneDriveDetails: IOneDriveRootDetails = oneDriveRootDetails;
      if (!oneDriveDetails) {
        oneDriveDetails = await getOneDriveRootDetails();
      }

      driveId = oneDriveDetails.driveId;
      siteUrl = oneDriveDetails.sharePointSiteUrl;
      sharePointToken = await authModule.getOneDriveAccessToken();
    }
    else {
      driveId = !currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId;
      siteUrl = currentDirectory.webUrl;
      sharePointToken = await authModule.getSharePointAccessToken();
    }

    const spItemUrlparts = selectedItem['.spItemUrl'].split('/');
    let itemId: string = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

    let pdfFileName = changeExtensionToPdf(selectedItem?.FileLeafRef);

    let destinationUrl: string;
    if (isInOneDrive) {
      destinationUrl = `${currentDirectory?.webUrl}${currentDirectory.directory ? `${currentDirectory.directory}` : ''}`
    } else if (currentDirectory?.isTeamsRootFolder && !currentDirectory.parentId) {
      destinationUrl = await getDriveRootWebUrl(currentDirectory.id);
    } else {
      destinationUrl = `${currentDirectory.webUrl}${currentDirectory.directory ? `/${currentDirectory.directory}` : ''}`;
    }

    const fileRequest: FilePreCommitRequest = {
      accessToken: sharePointToken,
      destinationUrl,
      siteUrl,
      fileName: pdfFileName,
      autoFolderCreation: false
    }

    const preCommitResult: IFilePreCommitResult = await getFilePreCommitInfo(fileRequest);

    let saveAndConvert = true;
    let isDuplicate = false;
    if (!preCommitResult || preCommitResult.exists === true) {
      isDuplicate = true;
      saveAndConvert = false;
    }

    if (currentSharepointFolder?.enableMinorVersions === true || isDuplicate) {
      openDuplicateFileVersionSelectorModal(isDuplicate, currentSharepointFolder?.enableMinorVersions === true);
      saveAndConvert = false;
    }

    if (saveAndConvert) {
      const request: ConvertAndSaveAsPDFRequest = {
        graphToken: await authModule.getGraphAccessToken(),
        sharePointToken,
        tenantGuid: tenantId,
        accountId,
        driveId,
        itemId,
        siteUrl,
        listId: currentDirectory.sharePointListId,
        teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
        channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
        fileUniqueId: selectedItem?.UniqueId.replace("{", "").replace("}", ""),
        EnsureUniqueFilename: false,
        checkinType: null,
      }
      try {
        await apiFileConvertAndSaveAsPDF(request);
        reloadGrid();
      } catch (error) {
        logger(error, "FileOptionsPanel.tsx (saveAsPdfFile)");
      }
      setWaitFor(false);
    }
  }

  return {
    menuItem_SaveAsPDF
  }
}